import useFilterWidgetFormContext from '../../../utils/useFilterWidgetFormContext';
import { Controller, useFieldArray } from 'react-hook-form';
import {
  defaultPredicate,
  FilterWidgetFormFieldPathType,
  IFilterPredicate,
} from '../../../types';
import { TextButton } from '../../../../Button';
import React from 'react';
import FilterWidgetSimplePredicate from './FilterWidgetSimplePredicate';
import * as FilterWidgetPredicateUI from './FilterWidgetPredicateUI';
import { IndexedIcon } from '../../../../Icon';
import FilterWidgetLogicOp from '../../FilterWidgetLogicOp';
import KubitTooltip from '../../../../KubitTooltip';

const MAX_PREDICATE_ELEMENTS = 5;

interface IProps {
  conditionIndex: number;
  predicateIndex: number;
  rootPredicate: IFilterPredicate;
  onUpdateRootPredicate: (newPredicate: IFilterPredicate) => void;
  onAddPredicate: () => void;
  allowMorePredicates: boolean;
  onRemove: () => void;
  disableRemove: boolean;
}

const FilterWidgetArrayObjectPredicate = ({
  conditionIndex,
  predicateIndex,
  rootPredicate,
  onUpdateRootPredicate,
  allowMorePredicates,
  onAddPredicate,
  onRemove,
  disableRemove,
}: IProps) => {
  const { control } = useFilterWidgetFormContext();

  const {
    fields: predicateElements,
    append: addPredicateElement,
    remove: removePredicateElement,
    update: updatePredicateElement,
  } = useFieldArray({
    name: `conditions.${conditionIndex}.predicates.${predicateIndex}.predicateElements`,
    control,
  });

  const handleAddPredicateElement = () => {
    addPredicateElement(defaultPredicate);
  };

  const handleRemovePredicateElement = (predicateElementIndex: number) => {
    // remove entire condition in case if there was a single predicate in it
    if (predicateElements.length === 1) {
      onRemove();
      return;
    }

    removePredicateElement(predicateElementIndex);
  };

  // Removing a Field will reset entire predicate
  // because predicate values rely on it
  const handleResetField = (predicateElementIndex: number) => {
    updatePredicateElement(predicateElementIndex, defaultPredicate);
  };

  const disableRemovePredicates =
    disableRemove && predicateElements.length === 1;

  const allowMoreElementPredicates =
    predicateElements.length < MAX_PREDICATE_ELEMENTS;

  return (
    <FilterWidgetPredicateUI.PredicateListContainer>
      <FilterWidgetPredicateUI.PredicateListIconContainer>
        <FilterWidgetPredicateUI.IconWrapper>
          {predicateIndex === 0 ? (
            <IndexedIcon
              name="filter"
              positiveIndex={conditionIndex + 1}
              isActive={true}
            />
          ) : (
            <Controller
              name={`conditions.${conditionIndex}.logicOps.${predicateIndex - 1}`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <FilterWidgetLogicOp
                  type="predicate"
                  operator={value}
                  onChange={onChange}
                />
              )}
            />
          )}
        </FilterWidgetPredicateUI.IconWrapper>

        <FilterWidgetPredicateUI.SimplePredicateContainerList
          $showBorder={predicateElements.length > 1}
        >
          {predicateElements.map((predicateElement, predicateElementIndex) => (
            <FilterWidgetSimplePredicate
              key={predicateElement.id}
              formPath={{
                type: FilterWidgetFormFieldPathType.ArrayObjectPredicate,
                conditionIndex,
                predicateIndex,
                predicateElementIndex,
              }}
              onResetField={() => handleResetField(predicateElementIndex)}
              onRemove={() =>
                handleRemovePredicateElement(predicateElementIndex)
              }
              disableRemove={disableRemovePredicates}
              rootPredicate={rootPredicate}
              onUpdateRootPredicate={onUpdateRootPredicate}
            />
          ))}
        </FilterWidgetPredicateUI.SimplePredicateContainerList>
      </FilterWidgetPredicateUI.PredicateListIconContainer>
      <FilterWidgetPredicateUI.PredicateListFooter>
        {allowMorePredicates && (
          <KubitTooltip content={'Apply another filter'} placement="bottom">
            <TextButton onClick={onAddPredicate}>+ Filter</TextButton>
          </KubitTooltip>
        )}

        {allowMoreElementPredicates && (
          <KubitTooltip
            placement="bottom"
            content={
              'Adding includes clause applies filter to the same object vs. across the array.'
            }
          >
            <TextButton onClick={handleAddPredicateElement}>
              + Includes
            </TextButton>
          </KubitTooltip>
        )}
      </FilterWidgetPredicateUI.PredicateListFooter>
    </FilterWidgetPredicateUI.PredicateListContainer>
  );
};

export default FilterWidgetArrayObjectPredicate;
